import jsPDF from 'jspdf';
import { Event, EventType } from '@/models/event';
import { Firestore } from 'firebase/firestore';
import { BRAND_COLOR, FONT_HELCH, PaperSize } from '../pdf-constants';
import { toAdHocPacificDate, getPacificMonthKey } from '../../date-utils';
import { fetchClubInfo, loadImage } from '../pdf-utils';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
    ];
}

function truncateText(doc: jsPDF, text: string, maxWidth: number): string {
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let t = text;
    while (t.length > 0 && doc.getTextWidth(t + '\u2026') > maxWidth) {
        t = t.slice(0, -1);
    }
    return t + '\u2026';
}

function buildDescription(ev: Event): string {
    const parts: string[] = [];
    if (ev.location) parts.push(ev.location);
    if (ev.description) parts.push(ev.description);
    if (ev.cd) parts.push(`CD: ${ev.cd}`);
    return parts.join(' \u2014 ');
}

export async function exportMonthlyListFormat(
    events: Event[],
    eventTypes: EventType[],
    getEventTypeName: (id: string) => string,
    firestore: Firestore,
    paperSize: PaperSize = 'letter',
    year?: number
) {
    const doc = new jsPDF({ format: paperSize });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const clubInfo = await fetchClubInfo(firestore);
    const scale = pageWidth / 215.9;

    const margin = 10;
    const colGap = 5;
    const colWidth = (pageWidth - margin * 2 - colGap) / 2;

    // Pre-load logo
    let logoImg: HTMLImageElement | null = null;
    let logoDrawW = 0;
    let logoDrawH = 0;

    if (clubInfo?.clubLogoUrl) {
        try {
            logoImg = await loadImage(clubInfo.clubLogoUrl);
            const targetH = 12 * scale;
            const imgAspect = logoImg.naturalWidth / logoImg.naturalHeight;
            if (imgAspect > 1) {
                logoDrawW = targetH;
                logoDrawH = targetH / imgAspect;
            } else {
                logoDrawH = targetH;
                logoDrawW = targetH * imgAspect;
            }
        } catch (e) {
            console.warn("Failed to preload logo", e);
        }
    }

    // Build event type color map
    const typeColorMap: Record<string, [number, number, number]> = {};
    const usedTypeIds = new Set<string>();
    eventTypes.forEach(et => {
        if (et.id && et.color) {
            typeColorMap[et.id] = hexToRgb(et.color);
        }
    });

    // Determine target year
    const targetYear = year || new Date().getFullYear();

    // Filter events to target year and sort
    const yearEvents = events
        .filter(e => {
            const key = getPacificMonthKey(e.date);
            return key.startsWith(String(targetYear));
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Track which types are actually used
    yearEvents.forEach(e => usedTypeIds.add(e.typeId));

    // Group by month
    const eventsByMonth: Record<string, Event[]> = {};
    for (let m = 1; m <= 12; m++) {
        const key = `${targetYear}-${String(m).padStart(2, '0')}`;
        eventsByMonth[key] = [];
    }
    yearEvents.forEach(e => {
        const key = getPacificMonthKey(e.date);
        if (eventsByMonth[key]) eventsByMonth[key].push(e);
    });

    // --- Header ---
    let headerBottom = 27;

    if (logoImg) {
        const headerLogoH = 15 * scale;
        const imgAspect = logoImg.naturalWidth / logoImg.naturalHeight;
        const headerLogoW = headerLogoH * imgAspect;
        const logoX = (pageWidth - headerLogoW) / 2;
        doc.addImage(logoImg, 'PNG', logoX, 5, headerLogoW, headerLogoH);

        const textY = 5 + headerLogoH + 4;
        doc.setFontSize(14);
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${clubInfo.clubName || 'SRAC'} \u2014 ${targetYear} Schedule of Events`, pageWidth / 2, textY, { align: 'center' });
        headerBottom = textY + 5;
    } else {
        doc.setFontSize(18);
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${targetYear} Schedule of Events`, pageWidth / 2, 18, { align: 'center' });
        headerBottom = 24;
    }

    // --- Color Key (legend) ---
    const usedTypes = eventTypes.filter(et => et.id && usedTypeIds.has(et.id));
    const keyY = headerBottom + 1;
    const dotR = 1.5;
    let keyX = margin;

    doc.setFontSize(7);
    doc.setFont(FONT_HELCH, 'normal');

    for (const et of usedTypes) {
        const color = typeColorMap[et.id!] || [150, 150, 150];
        doc.setFillColor(color[0], color[1], color[2]);
        doc.circle(keyX + dotR, keyY + dotR - 0.5, dotR, 'F');
        doc.setTextColor(80, 80, 80);
        doc.text(et.name, keyX + dotR * 2 + 2, keyY + dotR);
        keyX += doc.getTextWidth(et.name) + dotR * 2 + 6;
        if (keyX > pageWidth - 20) break;
    }

    const tableStartY = keyY + dotR * 2 + 3;

    // --- Footer dimensions ---
    const footerH = 16 * scale;
    const availableH = pageHeight - tableStartY - footerH - 3;

    // --- Split months into two columns ---
    const allMonthKeys = Object.keys(eventsByMonth).sort();
    const leftMonthKeys = allMonthKeys.slice(0, 6);   // Jan-Jun
    const rightMonthKeys = allMonthKeys.slice(6, 12);  // Jul-Dec

    // Count rows per column for font scaling
    function countRows(monthKeys: string[]): number {
        let count = 0;
        for (const key of monthKeys) {
            const evts = eventsByMonth[key];
            if (evts.length === 0) continue;
            count += 1; // month header
            for (const ev of evts) {
                count += 1; // event row
                if (buildDescription(ev)) count += 1; // desc row
            }
        }
        return count;
    }

    const maxRows = Math.max(countRows(leftMonthKeys), countRows(rightMonthKeys));

    // Calculate font size to fit the taller column on one page
    const baseEventFontSize = 9;
    const baseRowH = baseEventFontSize * 0.5 + 2;
    const neededH = maxRows * baseRowH;
    const fontScaleFactor = Math.min(1, availableH / neededH);
    const eventFontSize = Math.max(5, baseEventFontSize * fontScaleFactor);
    const descFontSize = Math.max(4, (baseEventFontSize - 2) * fontScaleFactor);
    const monthFontSize = Math.max(5.5, (baseEventFontSize + 1) * fontScaleFactor);
    const cellPad = Math.max(0.5, 1.5 * fontScaleFactor);

    // Row heights
    const monthRowH = monthFontSize * 0.45 + cellPad * 2 + 1;
    const eventRowH = eventFontSize * 0.4 + cellPad * 2;
    const descRowH = descFontSize * 0.35 + cellPad * 2;
    const dotSize = Math.max(0.8, eventFontSize * 0.18);

    // --- Draw column ---
    function drawColumn(monthKeys: string[], colX: number) {
        let curY = tableStartY;
        const dateColW = 36 * scale;
        const titleMaxW = colWidth - cellPad * 2 - dotSize * 3 - 2 - dateColW;

        for (const key of monthKeys) {
            const evts = eventsByMonth[key];
            if (evts.length === 0) continue;

            const [, mStr] = key.split('-');
            const monthName = MONTH_NAMES[parseInt(mStr) - 1];

            // Month header bar
            doc.setFillColor(245, 245, 245);
            doc.rect(colX, curY, colWidth, monthRowH, 'F');
            doc.setFont(FONT_HELCH, 'bold');
            doc.setFontSize(monthFontSize);
            doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
            doc.text(monthName, colX + cellPad, curY + monthRowH - cellPad - 0.5);
            curY += monthRowH;

            for (const ev of evts) {
                // Color dot
                const color = typeColorMap[ev.typeId] || [150, 150, 150];
                doc.setFillColor(color[0], color[1], color[2]);
                doc.circle(colX + cellPad + dotSize, curY + eventRowH / 2, dotSize, 'F');

                // Event title (bold)
                doc.setFont(FONT_HELCH, 'bold');
                doc.setFontSize(eventFontSize);
                doc.setTextColor(30, 30, 30);
                const titleX = colX + cellPad + dotSize * 3 + 1;
                const title = truncateText(doc, ev.title, titleMaxW);
                doc.text(title, titleX, curY + eventRowH - cellPad - 0.3);

                // Date + time (right-aligned)
                const d = toAdHocPacificDate(ev.date);
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                const timeStr = ev.time || '';
                const dateTime = timeStr ? `${dateStr} ${timeStr}` : dateStr;
                doc.setFont(FONT_HELCH, 'normal');
                doc.setTextColor(80, 80, 80);
                doc.text(dateTime, colX + colWidth - cellPad, curY + eventRowH - cellPad - 0.3, { align: 'right' });

                curY += eventRowH;

                // Light divider
                doc.setDrawColor(230, 230, 230);
                doc.setLineWidth(0.1);
                doc.line(colX + cellPad, curY, colX + colWidth - cellPad, curY);

                // Description row
                const desc = buildDescription(ev);
                if (desc) {
                    doc.setFont(FONT_HELCH, 'normal');
                    doc.setFontSize(descFontSize);
                    doc.setTextColor(140, 140, 140);
                    const descMaxW = colWidth - cellPad * 2 - 3;
                    const truncDesc = truncateText(doc, desc, descMaxW);
                    doc.text(truncDesc, colX + cellPad + 3, curY + descRowH - cellPad - 0.2);
                    curY += descRowH;
                }
            }

            // Small gap after each month
            curY += 1;
        }
    }

    drawColumn(leftMonthKeys, margin);
    drawColumn(rightMonthKeys, margin + colWidth + colGap);

    // --- Footer (matching flip calendar) ---
    const footerY = pageHeight - footerH;

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(margin, footerY, pageWidth - margin, footerY);

    if (logoImg) {
        const targetH = 12 * scale;
        const offX = (targetH - logoDrawW) / 2;
        const offY = (targetH - logoDrawH) / 2;
        doc.addImage(logoImg, 'PNG', margin + offX, footerY + 2 + offY, logoDrawW, logoDrawH);
        doc.setFontSize(10 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(clubInfo.clubName || 'SRAC', margin + targetH + 5, footerY + (targetH / 2) + 2);
    } else {
        doc.setFontSize(10 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'normal');
        doc.text(clubInfo.clubName || 'Snohomish Radio Aero Club', margin, footerY + 8);
    }

    if (clubInfo?.clubWebsite) {
        doc.setFontSize(9 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'normal');
        const userUrl = clubInfo.clubWebsite.replace(/^https?:\/\//, '');
        doc.text(userUrl, pageWidth - margin, footerY + 8, { align: 'right' });
    }

    doc.save(`srac-events-${targetYear}.pdf`);
}
